import { get, httpDelete, post, put } from '../../utils/http';
import { readSSEJsonStream } from '../../utils/sse';

const aiGatewayPath = (accountId, agentType, suffix = '') => `/accounts/${accountId}/ai-gateway/${agentType}${suffix}`;

const aiAgentPath = (accountId, agentType, suffix = '') => `/accounts/${accountId}/ai-agent/${agentType}${suffix}`;

const resolveAiAgentPath = (accountId, agentType, useGateway, gatewaySuffix, legacySuffix = gatewaySuffix) =>
  useGateway ? aiGatewayPath(accountId, agentType, gatewaySuffix) : aiAgentPath(accountId, agentType, legacySuffix);

// The event a thread is about rides the query string; `branchId` is a FastAPI alias, `project_id` raw.
// `body || {}`: post()'s default is an empty string, which the gateway 422s on.
export const createNewAiAgentChat = ({ agentType, accountId, body, useGateway, projectId, branchId }) =>
  post(resolveAiAgentPath(accountId, agentType, useGateway, '/new'), body || {}, {
    params: { project_id: projectId, branchId }
  });

export const renameAiAgentChatThread = ({ agentType, accountId, threadId, newName, useGateway }) =>
  put(resolveAiAgentPath(accountId, agentType, useGateway, `/${threadId}/rename`), { newName });

export const submitQuestionAiAgentChat = ({
  agentType,
  accountId,
  threadId,
  question,
  context,
  gatewayContext,
  useGateway,
  projectId,
  branchId
}) =>
  post(
    resolveAiAgentPath(accountId, agentType, useGateway, `/${threadId}/message`, `/${threadId}/question`),
    useGateway
      ? { message: question, ...(gatewayContext && { context: gatewayContext }) }
      : { question, ...(context && { context }) },
    { params: { project_id: projectId, branchId } }
  );

export const fetchQuestionResponseAiAgentChatStream = async ({
  agentType,
  accountId,
  threadId,
  questionId,
  onEvent,
  useGateway,
  projectId,
  branchId
}) =>
  readSSEJsonStream(
    resolveAiAgentPath(
      accountId,
      agentType,
      useGateway,
      `/${threadId}/message/${questionId}/stream`,
      `/${threadId}/question/${questionId}/stream`
    ),
    { onEvent, params: { project_id: projectId, branchId } }
  );

export const fetchAllAiAgentChatThreads = ({ agentType, accountId, useGateway }) =>
  get(resolveAiAgentPath(accountId, agentType, useGateway, ''));

// The event's conversation if it has one, else null — read only, unlike `/new` with a project_id.
export const fetchAiAgentSubjectThread = ({ agentType, accountId, projectId }) =>
  get(aiGatewayPath(accountId, agentType, '/subject-thread'), { project_id: projectId });

export const fetchAiAgentChatThread = ({ agentType, accountId, threadId, useGateway }) =>
  get(resolveAiAgentPath(accountId, agentType, useGateway, `/${threadId}`));

export const deleteAiAgentChatThread = ({ agentType, accountId, threadId, useGateway }) =>
  httpDelete(resolveAiAgentPath(accountId, agentType, useGateway, `/${threadId}`));

export const submitFeedbackAiAgentChat = ({
  agentType,
  accountId,
  threadId,
  questionId,
  rating,
  feedbackComment,
  useGateway
}) =>
  post(
    resolveAiAgentPath(
      accountId,
      agentType,
      useGateway,
      `/${threadId}/feedback?messageId=${questionId}`,
      `/${threadId}/feedback?questionId=${questionId}`
    ),
    { rating, feedbackComment }
  );

export const interruptAiAgentChat = ({ agentType, accountId, threadId, questionId, useGateway }) =>
  post(
    resolveAiAgentPath(
      accountId,
      agentType,
      useGateway,
      `/${threadId}/message/${questionId}/interrupt`,
      `/${threadId}/question/${questionId}/interrupt`
    )
  );

// Gateway-only: resolving an interactive card has no legacy equivalent.
export const submitAiGatewayAction = ({ accountId, agentType, threadId, componentId, action, payload }) =>
  post(aiGatewayPath(accountId, agentType, `/${threadId}/action`), { componentId, action, payload });

export const saveAiAgentRecipeFromTable = ({ agentType, accountId, datasetId, threadId, name, description }) =>
  post(aiAgentPath(accountId, agentType, '/recipes/save-dataset'), { datasetId, threadId, name, description });

export const fetchAllAiAgentRecipes = ({ agentType, accountId }) => get(aiAgentPath(accountId, agentType, '/recipes'));

export const runAiAgentRecipe = ({ agentType, accountId, recipeId }) =>
  get(aiAgentPath(accountId, agentType, `/recipes/${recipeId}/run`));

export const deleteAiAgentRecipe = ({ agentType, accountId, recipeId }) =>
  httpDelete(aiAgentPath(accountId, agentType, `/recipes/${recipeId}`));

export const renameAiAgentRecipe = ({ agentType, accountId, recipeId, newName }) =>
  put(aiAgentPath(accountId, agentType, `/recipes/${recipeId}`), { name: newName });
